import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, FINANCE_NAME } from '../../config';
import { formatDateToDDMMYYYY, getTodayYYYYMMDD } from '../../utils/dateUtils';
import './AdminDailyCashbook.css';

const COMPANY_ADDRESS = 'Kasgi , Osmanabad';

const ACCOUNT_GROUP_ORDER = [
  'General Loan A/C',
  'CD Loan A/C',
  'TBD Loan A/C',
  'General Loan EMI A/C',
  'CD Loan EMI A/C',
  'TBD Loan EMI A/C',
  'GLInterest A/c',
  'Cdinterest A/C',
  'TBDInterest A/C',
  'Pigmy A/C',
  'Pigmy Withdrawal A/C',
];

const splitAmountByMode = (amount, paymentMode) => {
  const value = Number(amount || 0);
  const isCash = String(paymentMode || 'Cash').toLowerCase() === 'cash';
  return {
    cash: isCash ? value : 0,
    transfer: isCash ? 0 : value,
    total: value,
  };
};

const formatStatementAmount = (value, showZero = false) => {
  const amount = Number(value || 0);
  if (!showZero && amount === 0) return '-';
  return amount.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatPrintDate = (value) => {
  if (!value) return '';
  const datePart = String(value).slice(0, 10);
  return formatDateToDDMMYYYY(datePart);
};

const buildParticulars = (entry) => {
  const prefix = entry.side === 'credit' ? 'By.' : 'To.';
  const mode = entry.payment_mode || 'Cash';
  const accountPart = entry.account_no ? `${entry.account_no} ` : '';
  const name = String(entry.party_name || '').trim() || 'N/A';
  return `${prefix} ${accountPart}${mode} ${name}`.replace(/\s+/g, ' ').trim();
};

const AdminDailyCashbook = () => {
  const [selectedDate, setSelectedDate] = useState(getTodayYYYYMMDD());
  const [cashbook, setCashbook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchCashbook = useCallback(async () => {
    if (!selectedDate) {
      setError('Please select a date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/daily-cashbook?date=${encodeURIComponent(selectedDate)}`
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch cashbook.');
      }
      const data = await response.json();
      setCashbook(data);
    } catch (err) {
      console.error('Admin cashbook fetch failed:', err);
      setError(err.message || 'Unexpected error while loading cashbook.');
      setCashbook(null);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchCashbook();
  }, [fetchCashbook]);

  const groupedRows = useMemo(() => {
    const entries = cashbook?.entries || [];
    const groups = new Map();

    entries.forEach((entry) => {
      const key = entry.accountGroup || 'Other';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(entry);
    });

    const orderedKeys = [
      ...ACCOUNT_GROUP_ORDER.filter((key) => groups.has(key)),
      ...Array.from(groups.keys()).filter((key) => !ACCOUNT_GROUP_ORDER.includes(key)),
    ];

    return orderedKeys.map((accountGroup) => ({
      accountGroup,
      entries: groups.get(accountGroup) || [],
    }));
  }, [cashbook]);

  const totals = cashbook?.totals || {
    totalDebit: 0,
    totalCredit: 0,
    totalCreditCash: 0,
    totalCreditTransfer: 0,
    totalDebitCash: 0,
    totalDebitTransfer: 0,
    creditGrandTotal: 0,
    debitGrandTotal: 0,
  };

  const openingBalance = Number(cashbook?.openingBalance || 0);
  const closingBalance = Number(cashbook?.closingBalance || 0);
  const creditGrandTotal = totals.creditGrandTotal ?? openingBalance + totals.totalCredit;
  const debitGrandTotal = totals.debitGrandTotal ?? closingBalance + totals.totalDebit;
  const displayDate = formatPrintDate(cashbook?.date || selectedDate);
  const companyName = FINANCE_NAME || 'SHREE DHANESHWARI FINANCE AND LEASING';

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const renderAmountCell = (side, entrySide, amounts, field) => {
    if (entrySide !== side) return '-';
    return formatStatementAmount(amounts[field]);
  };

  const buildPrintTableRows = () => {
    if (groupedRows.length === 0) {
      return `<tr><td colspan="8" class="no-data">No transactions found for ${escapeHtml(displayDate)}</td></tr>`;
    }

    let html = '';
    groupedRows.forEach((group) => {
      html += `<tr class="account-group-row"><td colspan="8">${escapeHtml(group.accountGroup)}</td></tr>`;
      group.entries.forEach((entry) => {
        const amounts = splitAmountByMode(entry.amount, entry.payment_mode);
        const creditAmounts = entry.side === 'credit' ? amounts : { cash: 0, transfer: 0, total: 0 };
        const debitAmounts = entry.side === 'debit' ? amounts : { cash: 0, transfer: 0, total: 0 };
        html += `
          <tr>
            <td class="particulars-cell">${escapeHtml(buildParticulars(entry))}</td>
            <td class="vno-cell">${escapeHtml(entry.vNo || '-')}</td>
            <td class="amount-col">${renderAmountCell('credit', entry.side, creditAmounts, 'cash')}</td>
            <td class="amount-col">${renderAmountCell('credit', entry.side, creditAmounts, 'transfer')}</td>
            <td class="amount-col">${renderAmountCell('credit', entry.side, creditAmounts, 'total')}</td>
            <td class="amount-col">${renderAmountCell('debit', entry.side, debitAmounts, 'cash')}</td>
            <td class="amount-col">${renderAmountCell('debit', entry.side, debitAmounts, 'transfer')}</td>
            <td class="amount-col">${renderAmountCell('debit', entry.side, debitAmounts, 'total')}</td>
          </tr>`;
      });
    });
    return html;
  };

  const handlePrint = () => {
    if (!cashbook) return;

    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      alert('Please allow popups to print the day book.');
      return;
    }

    const tableBody = buildPrintTableRows();
    const printHtml = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Daily Cash &amp; Day Book - ${escapeHtml(displayDate)}</title>
          <style>
            @page { size: A4 portrait; margin: 10mm 8mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 0;
              color: #000;
              background: #fff;
              font-family: Arial, sans-serif;
            }
            .daybook-statement {
              width: 100%;
              padding: 0;
            }
            .daybook-header {
              text-align: center;
              margin-bottom: 10px;
              font-family: 'Times New Roman', Times, serif;
            }
            .daybook-header-top {
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 12px;
              margin-bottom: 4px;
            }
            .page-info, .daybook-date {
              font-family: 'Courier New', monospace;
            }
            .company-name {
              margin: 0;
              color: #c00000;
              font-size: 20px;
              font-weight: 800;
            }
            .company-address {
              margin: 4px 0 8px;
              font-size: 13px;
            }
            .report-title {
              margin: 0 0 6px;
              color: #1a4fd6;
              font-size: 18px;
              font-weight: 800;
            }
            .daybook-table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
              table-layout: fixed;
              font-size: 11px;
            }
            .daybook-table th,
            .daybook-table td {
              border: 1px solid #555;
              padding: 3px 5px;
              line-height: 1.2;
              word-wrap: break-word;
            }
            .daybook-table th {
              text-align: center;
              font-weight: 700;
              background: #fff;
              border-bottom: 2px solid #000;
            }
            .particulars-col { width: 24%; }
            .vno-col { width: 5%; }
            .amount-col {
              width: 9%;
              text-align: right;
              font-family: 'Courier New', monospace;
              white-space: nowrap;
              font-size: 10px;
              padding: 3px 4px;
            }
            .particulars-cell { text-align: left; }
            .vno-cell { text-align: center; font-family: 'Courier New', monospace; }
            .account-group-row td {
              color: #c00000;
              font-weight: 700;
              font-size: 12px;
            }
            .summary-row td { font-weight: 700; }
            .total-row td { border-top: 3px double #000; }
            .balance-label-cell {
              text-align: right;
              white-space: nowrap;
              font-size: 11px;
            }
            .balance-amount-cell {
              text-align: right;
              white-space: nowrap;
              font-family: 'Courier New', monospace;
              font-size: 11px;
              padding: 4px 8px;
            }
            .grand-total { font-weight: 800; }
            .final-grand-row td { border-bottom: 2px solid #000; }
            .final-grand-row .final-total { border-top: 1px solid #000; }
            .no-data { text-align: center; padding: 16px; }
            .daybook-footer {
              margin-top: 8px;
              text-align: center;
              font-family: 'Times New Roman', Times, serif;
              font-size: 12px;
            }
            .closing-balance-line { margin: 0 0 4px; }
            .end-statement { margin: 0; letter-spacing: 1px; }
          </style>
        </head>
        <body>
          <div class="daybook-statement">
            <div class="daybook-header">
              <div class="daybook-header-top">
                <span class="page-info">Page 1 of 1</span>
                <span class="daybook-date">Date : ${escapeHtml(displayDate)}</span>
              </div>
              <h1 class="company-name">${escapeHtml(companyName)}</h1>
              <p class="company-address">${escapeHtml(COMPANY_ADDRESS)}</p>
              <h2 class="report-title">Daily Cash &amp; Day Book</h2>
            </div>
            <table class="daybook-table">
              <thead>
                <tr>
                  <th rowspan="2" class="particulars-col">Particulars</th>
                  <th rowspan="2" class="vno-col">V No</th>
                  <th colspan="3">Credit</th>
                  <th colspan="3">Debit</th>
                </tr>
                <tr>
                  <th class="amount-col">Cash</th>
                  <th class="amount-col">Transfer</th>
                  <th class="amount-col">Total</th>
                  <th class="amount-col">Cash</th>
                  <th class="amount-col">Transfer</th>
                  <th class="amount-col">Total</th>
                </tr>
              </thead>
              <tbody>
                ${tableBody}
                <tr class="summary-row total-row">
                  <td colspan="2">Total</td>
                  <td class="amount-col">${formatStatementAmount(totals.totalCreditCash, true)}</td>
                  <td class="amount-col">${formatStatementAmount(totals.totalCreditTransfer, true)}</td>
                  <td class="amount-col">${formatStatementAmount(totals.totalCredit, true)}</td>
                  <td class="amount-col">${formatStatementAmount(totals.totalDebitCash, true)}</td>
                  <td class="amount-col">${formatStatementAmount(totals.totalDebitTransfer, true)}</td>
                  <td class="amount-col">${formatStatementAmount(totals.totalDebit, true)}</td>
                </tr>
                <tr class="summary-row grand-total-row final-grand-row">
                  <td colspan="2"></td>
                  <td class="amount-col">-</td>
                  <td class="amount-col">-</td>
                  <td class="amount-col grand-total final-total">${formatStatementAmount(creditGrandTotal, true)}</td>
                  <td class="amount-col">-</td>
                  <td class="amount-col">-</td>
                  <td class="amount-col grand-total final-total">${formatStatementAmount(debitGrandTotal, true)}</td>
                </tr>
              </tbody>
            </table>
            <div class="daybook-footer">
              <p class="closing-balance-line">For the Day Closing Balance : <strong>${closingBalance.toFixed(2)}</strong></p>
              <p class="end-statement">+++ END OF STATEMENT +++</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          <\/script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  return (
    <div className="admin-cashbook-container">
      <div className="admin-cashbook-toolbar no-print">
        <div className="toolbar-title">
          <h2>Daily Cash &amp; Day Book</h2>
          <p>Select a date to view the day book statement</p>
        </div>
        <div className="admin-cashbook-filter">
          <div className="filter-group">
            <label htmlFor="cashbook-date">Date</label>
            <input
              id="cashbook-date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          <button className="apply-button" onClick={fetchCashbook} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </button>
          <button
            className="apply-button secondary"
            onClick={() => setSelectedDate(getTodayYYYYMMDD())}
            disabled={loading}
          >
            Today
          </button>
          <button className="apply-button print-btn" onClick={handlePrint} disabled={loading || !cashbook}>
            Print
          </button>
        </div>
      </div>

      {error ? <div className="admin-cashbook-error no-print">{error}</div> : null}

      {loading && !cashbook ? (
        <div className="admin-cashbook-loading">Loading cashbook data…</div>
      ) : null}

      {cashbook ? (
        <div className="daybook-statement">
          <div className="daybook-header">
            <div className="daybook-header-top">
              <span className="page-info">Page 1 of 1</span>
              <span className="daybook-date">Date : {displayDate}</span>
            </div>
            <h1 className="company-name">{companyName}</h1>
            <p className="company-address">{COMPANY_ADDRESS}</p>
            <h2 className="report-title">Daily Cash &amp; Day Book</h2>
          </div>

          <div className="admin-cashbook-table-wrapper">
            <table className="admin-cashbook-table daybook-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="particulars-col">Particulars</th>
                  <th rowSpan="2" className="vno-col">V No</th>
                  <th colSpan="3" className="credit-header">Credit</th>
                  <th colSpan="3" className="debit-header">Debit</th>
                </tr>
                <tr>
                  <th className="amount-col">Cash</th>
                  <th className="amount-col">Transfer</th>
                  <th className="amount-col">Total</th>
                  <th className="amount-col">Cash</th>
                  <th className="amount-col">Transfer</th>
                  <th className="amount-col">Total</th>
                </tr>
              </thead>
              <tbody>
                {groupedRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="admin-cashbook-no-data">
                      No transactions found for {displayDate}
                    </td>
                  </tr>
                ) : (
                  groupedRows.map((group) => (
                    <React.Fragment key={group.accountGroup}>
                      <tr className="account-group-row">
                        <td colSpan="8">{group.accountGroup}</td>
                      </tr>
                      {group.entries.map((entry, index) => {
                        const amounts = splitAmountByMode(entry.amount, entry.payment_mode);
                        const creditAmounts =
                          entry.side === 'credit' ? amounts : { cash: 0, transfer: 0, total: 0 };
                        const debitAmounts =
                          entry.side === 'debit' ? amounts : { cash: 0, transfer: 0, total: 0 };

                        return (
                          <tr key={`${group.accountGroup}-${entry.vNo}-${index}`}>
                            <td className="particulars-cell">{buildParticulars(entry)}</td>
                            <td className="vno-cell">{entry.vNo || '-'}</td>
                            <td className="amount-col">
                              {entry.side === 'credit' ? formatStatementAmount(creditAmounts.cash) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'credit' ? formatStatementAmount(creditAmounts.transfer) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'credit' ? formatStatementAmount(creditAmounts.total) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'debit' ? formatStatementAmount(debitAmounts.cash) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'debit' ? formatStatementAmount(debitAmounts.transfer) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'debit' ? formatStatementAmount(debitAmounts.total) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))
                )}

                <tr className="summary-row total-row">
                  <td colSpan="2" className="summary-label">Total</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalCreditCash, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalCreditTransfer, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalCredit, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalDebitCash, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalDebitTransfer, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalDebit, true)}</td>
                </tr>

                <tr className="summary-row grand-total-row final-grand-row">
                  <td colSpan="2" />
                  <td className="amount-col">-</td>
                  <td className="amount-col">-</td>
                  <td className="amount-col grand-total final-total">
                    {formatStatementAmount(creditGrandTotal, true)}
                  </td>
                  <td className="amount-col">-</td>
                  <td className="amount-col">-</td>
                  <td className="amount-col grand-total final-total">
                    {formatStatementAmount(debitGrandTotal, true)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="daybook-footer">
            <p className="closing-balance-line">
              For the Day Closing Balance : <strong>{closingBalance.toFixed(2)}</strong>
            </p>
            <p className="end-statement">+++ END OF STATEMENT +++</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminDailyCashbook;
